import { useQuery } from '@tanstack/react-query';
import { ResponseError } from 'superagent';
import { ApiClient } from 'src/client/helpers';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import { SignedSfwPage, SignedSfwViewerImages } from 'src/shared/sfwImages';
import urls, { constructUrl } from 'src/shared/urls';

const refreshBeforeExpiry = (data?: { refreshAfter: number }) =>
  data ? Math.max(1000, data.refreshAfter - Date.now()) : false;

const fetchSignedUrls = async <T>(
  endpoint: string,
  params: Record<string, number>,
  signal?: AbortSignal,
): Promise<T> => {
  const request = new ApiClient().get(endpoint, { params });
  const abortRequest = () => request.abort();
  signal?.addEventListener('abort', abortRequest);

  try {
    const { body } = await request;
    return body as T;
  } finally {
    signal?.removeEventListener('abort', abortRequest);
  }
};

export const useSfwImagePage = (page: number) => {
  const siteUUID = useSelector((state) => state.site.UUID);
  const userUUID = useSelector((state) => state.auth.user?.UUID);

  return useQuery<SignedSfwPage, ResponseError>({
    queryFn: ({ signal }) => fetchSignedUrls(constructUrl(urls.get.sfwImages), { page }, signal),
    queryKey: ['sfw-image-page', siteUUID, userUUID, page],
    refetchInterval: refreshBeforeExpiry,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: false,
  });
};

export const useSfwViewerImages = (number: number) => {
  const siteUUID = useSelector((state) => state.site.UUID);
  const userUUID = useSelector((state) => state.auth.user?.UUID);

  return useQuery<SignedSfwViewerImages, ResponseError>({
    queryFn: ({ signal }) => fetchSignedUrls(constructUrl(urls.get.sfwImage), { number }, signal),
    queryKey: ['sfw-viewer-images', siteUUID, userUUID, number],
    refetchInterval: refreshBeforeExpiry,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: false,
  });
};
